import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Search, X } from 'lucide-react';
import {
  ALL_DISPUTE_REASONS,
  DISPUTE_REASON_DESCRIPTION_FR,
  DISPUTE_REASON_LABEL_FR,
  type DisputeReason,
} from '../../i18n/dispute-reasons.js';
import { DISPUTE_MOTIF_CATEGORIES } from './disputeMotifCategories.js';
import { formatAmount } from './format.js';

const MAX_COMMENT = 4000;

interface Props {
  invoiceNumber: string;
  // REQ-FE-033 area B9: header copy expands beyond invoice number to
  // include supplier name and total. Both are optional for legacy callers.
  supplierName?: string | null;
  amountTotal?: number | null;
  currency?: string | null;
  busy: boolean;
  onSubmit: (payload: { reason: DisputeReason; comment: string }) => void;
  onClose: () => void;
}

// REQ-FE-033 area B — Dispute modal ported from the mockup source at
// `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/components/DisputeSheet.tsx`.
// Bottom-anchored sheet, ~768px (max-w-3xl), 2-col grid of motif cards
// (radio + label + hint + code chip), kbd shortcuts in the footer.
export function DisputeModal({
  invoiceNumber,
  supplierName,
  amountTotal,
  currency,
  busy,
  onSubmit,
  onClose,
}: Props) {
  const [reason, setReason] = useState<DisputeReason | null>(null);
  const [comment, setComment] = useState('');
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // 50ms delay matches the mockup — gives the sheet-in animation time
    // to settle before stealing focus.
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, []);

  const lowerQ = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!lowerQ) return DISPUTE_MOTIF_CATEGORIES;
    return DISPUTE_MOTIF_CATEGORIES.map((g) => ({
      ...g,
      reasons: g.reasons.filter((r) =>
        `${DISPUTE_REASON_LABEL_FR[r]} ${DISPUTE_REASON_DESCRIPTION_FR[r] ?? ''} ${r}`
          .toLowerCase()
          .includes(lowerQ),
      ),
    })).filter((g) => g.reasons.length > 0);
  }, [lowerQ]);

  const trimmedComment = comment.trimEnd();
  const submitDisabled = busy || reason === null || trimmedComment.length > MAX_COMMENT;

  function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    if (submitDisabled || !reason) return;
    onSubmit({ reason, comment: trimmedComment });
  }

  // REQ-FE-033 area B11 — Esc closes, ⌘/Ctrl+Enter submits.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && reason) {
        e.preventDefault();
        if (!submitDisabled) onSubmit({ reason, comment: trimmedComment });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onSubmit, reason, submitDisabled, trimmedComment]);

  const motifCount = ALL_DISPUTE_REASONS.length;
  const amountStr =
    typeof amountTotal === 'number' ? formatAmount(amountTotal, currency ?? null) : null;

  return (
    <div
      className="dispute-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="Mettre en litige"
      data-testid="dispute-modal"
    >
      <button
        type="button"
        className="dispute-sheet__scrim"
        onClick={onClose}
        aria-label="Fermer"
      />

      <div className="dispute-sheet__panel" data-testid="dispute-modal-panel">
        {/* Header — eyebrow ("Mettre en litige · supplier") then h1
            (reference + amount inline). */}
        <header className="dispute-sheet__head">
          <div className="dispute-sheet__head-text">
            <p className="eyebrow dispute-sheet__eyebrow">
              Mettre en litige
              {supplierName ? (
                <>
                  {' · '}
                  <span data-testid="dispute-modal-supplier">{supplierName}</span>
                </>
              ) : null}
            </p>
            <h2 id="dispute-modal-title" className="display dispute-sheet__title">
              {invoiceNumber}
              {amountStr ? (
                <span className="dispute-sheet__title-amount" data-testid="dispute-modal-amount">
                  {amountStr}
                </span>
              ) : null}
            </h2>
          </div>
          <button
            type="button"
            className="dispute-sheet__close"
            onClick={onClose}
            aria-label="Fermer"
            title="Échap"
            data-testid="dispute-modal-close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </header>

        {/* Search bar */}
        <div className="dispute-sheet__search-wrap">
          <div className="dispute-sheet__search">
            <Search size={14} strokeWidth={1.5} className="dispute-sheet__search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="dispute-sheet__search-input"
              placeholder={`Rechercher un motif… (${motifCount} disponibles)`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="dispute-reason-search"
            />
            {query ? (
              <button
                type="button"
                className="dispute-sheet__search-clear"
                onClick={() => setQuery('')}
              >
                Effacer
              </button>
            ) : null}
          </div>
        </div>

        {/* Reasons list — 7 categories, 2-col grid per category. */}
        <form onSubmit={handleSubmit} className="dispute-sheet__body" aria-label="Motif du litige">
          <div
            className="dispute-sheet__reasons"
            role="radiogroup"
            aria-label="Motif"
            data-testid="dispute-modal-grid"
          >
            {filteredGroups.length === 0 ? (
              <p className="muted dispute-sheet__empty">
                Aucun motif ne correspond à votre recherche.
              </p>
            ) : (
              filteredGroups.map((g) => (
                <fieldset
                  key={g.id}
                  className="dispute-sheet__category"
                  data-testid={`dispute-category-${g.id}`}
                >
                  <legend
                    className="eyebrow dispute-sheet__category-label"
                    data-testid={`dispute-category-label-${g.id}`}
                  >
                    {g.label}
                  </legend>
                  <div className="dispute-sheet__cards">
                    {g.reasons.map((r) => {
                      const isActive = reason === r;
                      return (
                        <label
                          key={r}
                          className={`dispute-sheet__card${isActive ? ' is-active' : ''}`}
                          data-testid={`dispute-reason-${r}`}
                        >
                          <input
                            type="radio"
                            name="dispute-reason"
                            value={r}
                            checked={isActive}
                            onChange={() => setReason(r)}
                            className="dispute-sheet__card-radio"
                          />
                          <span className="dispute-sheet__card-body">
                            <span className="dispute-sheet__card-label">
                              {DISPUTE_REASON_LABEL_FR[r]}
                            </span>
                            <span className="dispute-sheet__card-hint">
                              {DISPUTE_REASON_DESCRIPTION_FR[r]}
                            </span>
                            <code
                              className="dispute-sheet__card-code"
                              data-testid={`dispute-reason-${r}-code`}
                            >
                              {r}
                            </code>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ))
            )}
          </div>

          {/* Comment — facultatif label, rows=2 textarea. */}
          <div className="dispute-sheet__comment-wrap">
            <label className="eyebrow dispute-sheet__comment-label" htmlFor="dispute-comment">
              Commentaire libre (facultatif)
            </label>
            <textarea
              id="dispute-comment"
              className="dispute-sheet__comment"
              placeholder="Précisez à destination du fournisseur…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={MAX_COMMENT + 100}
              rows={2}
              data-testid="dispute-comment"
            />
          </div>

          {/* Footer — kbd shortcuts left, Annuler + CTA right. */}
          <footer className="dispute-sheet__foot">
            <p className="dispute-sheet__shortcuts" aria-hidden="true">
              <kbd>Esc</kbd>
              <span className="muted">annuler</span>
              <span className="dispute-sheet__shortcuts-sep">·</span>
              <kbd>⌘</kbd>
              <kbd>⏎</kbd>
              <span className="muted">confirmer</span>
            </p>
            <div className="dispute-sheet__foot-actions">
              <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitDisabled}
                data-testid="dispute-submit"
              >
                {busy ? 'Enregistrement…' : 'Enregistrer le litige'}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

// re-export for caller introspection
export { ALL_DISPUTE_REASONS };
